import videojs from 'video.js';
import 'videojs-errors';

import { IApplicationData } from 'src/interfaces/ApplicationData';
import { IMarkerService } from 'src/interfaces/MarkerService';
import VideoJsConfigDirector from 'src/builders/VideoJsConfigDirector';
import VideoJsPlayerFactory from 'src/core/player/factories/VideoJsPlayerFactory';
import { TRANSMISSION_TYPE } from 'src/interfaces/enums';
import { IVideoJSPlayer } from 'src/interfaces/VideoJS';
import { muxEmit } from 'src/utils/analyticsUtils';
import Player from 'src/core/player/entities/Player';
import MarkerServiceV1 from '../MarkerService/MarkerServiceV1';
import ProgressService from '../ProgressService';
import DatahubService from '../DatahubService';
import MessageEventService from '../MessageEventService';
import UserPreferencesService from '../UserPreferencesService';
import MarkerServiceV2 from '../MarkerService/MarkerServiceV2';
import LiveService from '../LiveService';
import PlayerService from '../PlayerService';
import SubtitleService from '../SubtitleService';

class ApplicationService {
  public applicationData: Readonly<IApplicationData>;

  public datahub: DatahubService;
  public userPreferences: UserPreferencesService;
  public marker: IMarkerService;

  public player?: PlayerService | Player;
  public progress?: ProgressService;
  public messageEvent?: MessageEventService;
  public subtitle: SubtitleService;

  init(applicationData: IApplicationData) {
    this.applicationData = applicationData as IApplicationData;
    this.userPreferences = new UserPreferencesService(applicationData);

    if (applicationData.isJwtSignature) this.marker = new MarkerServiceV2(applicationData);
    else this.marker = new MarkerServiceV1(applicationData);
  }

  get isDRMEnabled() {
    return this.applicationData.isDrmEnabled;
  }

  async setPlayer(videoElement: HTMLVideoElement, callback?: () => void) {
    const { applicationData } = this;

    if (!applicationData.flags.ENABLE_PLAYER_CORE_REFACTOR) {
      this.player = (await this.createPlayerLegacy(videoElement, callback)) as unknown as Player;
    } else this.player = this.createPlayer(videoElement, callback);

    this.datahub = new DatahubService(applicationData, this.player.viewId);
    this.progress = new ProgressService(applicationData, this.player);
    this.messageEvent = new MessageEventService(applicationData, this.player);
    this.subtitle = new SubtitleService(this.player);
  }

  private createPlayer(player: HTMLVideoElement, callback?: () => void) {
    const { applicationData } = this;

    const playerFactory = new VideoJsPlayerFactory(applicationData);
    return playerFactory.createPlayer(applicationData.transmissionType, player, callback);
  }

  private async createPlayerLegacy(element: HTMLVideoElement, callback?: () => void) {
    const { applicationData } = this;
    const config = this.getPlayerConfig();

    const playerInstance = videojs(element, config, callback) as IVideoJSPlayer;

    playerInstance.errors();

    playerInstance.errors.timeout(-1);

    window.PLAYER = playerInstance;

    const player =
      applicationData.transmissionType === TRANSMISSION_TYPE.live
        ? new LiveService(playerInstance)
        : new PlayerService(playerInstance);

    this.player = player as unknown as Player;

    if (this.isDRMEnabled) await player?.enableDRM();
    muxEmit(playerInstance);

    return player;
  }

  getPlayerConfig() {
    const { applicationData } = this;

    const videojsConfigDirector = new VideoJsConfigDirector(applicationData);
    return videojsConfigDirector.buildVideoJsConfig();
  }

  clean() {
    this.player?.clean();
    this.player = undefined;
  }
}

export default new ApplicationService();
